import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SupplierSelectionResult, SupplierService } from '@lib/supplier';
import { DialogOptions } from '@lib/util';
import { DemandSupplier } from '../../types/IDemandSupplierContact';
import { PaymentTerm } from '@lib/payment-terms';
import { FrameworkAgreement } from './demand-supplier-framework-agreement/SupplierFrameworkAgreement';
import { SupplierFrameworkAgreementDataService } from './demand-supplier-framework-agreement/demand-supplier-framework-agreement-data.service';
import {
    ChangeSupplierContactDialogComponent,
    ChangeSupplierContactDialogData,
    ChangeSupplierContactDialogResult,
} from './change-supplier-contact-dialog/change-supplier-contact-dialog.component';

export interface IDemandSupplierContactChange {
    supplier: DemandSupplier;
    contactId: string;
}

@Component({
    selector: 'demand-supplier-display',
    templateUrl: './demand-supplier-display.component.html',
    styleUrls: ['./demand-supplier-display.component.scss'],
    standalone: false,
})
export class DemandSupplierDisplayComponent {
    supplierService = inject(SupplierService);
    private readonly dialog = inject(MatDialog);
    private readonly supplierFrameworkAgreementDataService = inject(SupplierFrameworkAgreementDataService);

    @Input() demandId!: string;
    @Input() suppliers!: DemandSupplier[];
    @Input() readonly = true;
    @Input() required = false;
    @Input() isValid = false;
    @Input() supplierChangeAllowed = true;
    @Input() companyCodeId: string | null = null;
    /**
     * Currency the demand is written in (EPROC-11425). While it is set, suppliers that demonstrably
     * do not accept it are filtered out of the selection dialog and named in a hint there; empty as
     * long as the demand has no position, which leaves the selection unfiltered.
     */
    @Input() currency: string | null = null;
    /**
     * EPROC-11560: reason why the document currency is not accepted by the selected supplier. Shown
     * at the top of the supplier card while the order release is blocked because of it — the parent
     * only sets it when the flow actually ends in an order (EPROC-11425/11560 Konsolidierung).
     */
    @Input() currencyConflictMessage: string | null = null;
    /** EPROC-11560: technical check failure shown with a retry action while the order release stays blocked. */
    @Input() currencyCheckErrorMessage: string | null = null;
    /**
     * EPROC-11425 reopen #5b: true while the currency check is still running on the order path. The
     * running check blocks the release like a conflict does, so it needs a visible form too —
     * without it the approver saw a dead release button with no reason anywhere in the UI.
     */
    @Input() currencyCheckPending = false;

    @Output() supplierSelected: EventEmitter<DemandSupplier[]> = new EventEmitter<DemandSupplier[]>();
    @Output() supplierRemoved: EventEmitter<DemandSupplier> = new EventEmitter<DemandSupplier>();
    @Output() supplierChanged: EventEmitter<DemandSupplier> = new EventEmitter<DemandSupplier>();
    @Output() supplierContactChanged: EventEmitter<IDemandSupplierContactChange> =
        new EventEmitter<IDemandSupplierContactChange>();
    @Output() currencyCheckRetry: EventEmitter<void> = new EventEmitter<void>();

    public openSupplierSelection(): void {
        const companyCodeId = this.companyCodeId?.trim();
        if (!companyCodeId) {
            return;
        }

        const alreadySelectedSuppliers = this.suppliers?.map((s) => s.companyId.value);
        this.supplierService
            .selectSupplier(alreadySelectedSuppliers, true, true, false, companyCodeId, this.currency ?? undefined)
            .subscribe((result: SupplierSelectionResult | undefined) => {
                if (result) {
                    const mappedSuppliers = result
                        .filter((selectedSupplier) => !!selectedSupplier.contact)
                        .map((selectedSupplier) => DemandSupplier.fromSelectedSupplier(selectedSupplier));
                    this.supplierSelected.emit(mappedSuppliers);
                }
            });
    }

    public openChangeContactDialog(supplier: DemandSupplier): void {
        this.dialog
            .open<
                ChangeSupplierContactDialogComponent,
                ChangeSupplierContactDialogData,
                ChangeSupplierContactDialogResult
            >(
                ChangeSupplierContactDialogComponent,
                DialogOptions.SMALL(
                    new ChangeSupplierContactDialogData(
                        supplier.companyId.value,
                        supplier.companyName,
                        supplier.contact?.id ?? null,
                        this.companyCodeId,
                    ),
                ),
            )
            .afterClosed()
            .subscribe((result: ChangeSupplierContactDialogResult | undefined) => {
                if (result?.contactId) {
                    this.supplierContactChanged.emit({ supplier, contactId: result.contactId });
                }
            });
    }

    public paymentTermDeleted(supplier: DemandSupplier): void {
        supplier.deletePaymentTerm();
        this.supplierChanged.emit(supplier);
    }

    public removeSupplier(supplier: DemandSupplier): void {
        const currentFrameworkAgreement = supplier.supplierFrameworkAgreement;
        if (currentFrameworkAgreement) {
            this.supplierFrameworkAgreementDataService
                .deleteSupplierFrameworkAgreement(this.demandId, supplier.companyId.value, currentFrameworkAgreement)
                .subscribe({
                    next: () => {
                        supplier.supplierFrameworkAgreement = undefined;
                        this.supplierRemoved.emit(supplier);
                    },
                    error: () => {
                        supplier.supplierFrameworkAgreement = undefined;
                        this.supplierRemoved.emit(supplier);
                    },
                });
        } else {
            this.supplierRemoved.emit(supplier);
        }
    }

    protected readonly event = event;

    public paymentTermUpdated(supplier: DemandSupplier, $updatedPaymentTerm: PaymentTerm) {
        supplier.updatePaymentTerm($updatedPaymentTerm);
        this.supplierChanged.emit(supplier);
    }

    public incotermUpdated(supplier: DemandSupplier, incotermCode: string | null): void {
        supplier.updateIncoterm(incotermCode);
        this.supplierChanged.emit(supplier);
    }

    public onDeleteSupplierFrameworkAgreement(supplier: DemandSupplier): void {
        supplier.supplierFrameworkAgreement = undefined;
        this.supplierChanged.emit(supplier);
    }

    public OnUpdateSupplierFrameworkAgreement(supplier: DemandSupplier, newFrameworkAgreement: FrameworkAgreement) {
        supplier.supplierFrameworkAgreement = newFrameworkAgreement;
        this.supplierChanged.emit(supplier);
    }

    public hasEmail(supplier: DemandSupplier): boolean {
        return !!supplier.email && supplier.email.trim().length > 0;
    }
}
